import {type CSSProperties, useMemo} from 'react'
import type {ColumnInstance} from 'react-table'

import {CELL_HEIGHT, TABLE_HEADER_HEIGHT} from './constants'
import {useTableColumnResizing} from './table-provider'

type CellStyleProps = Pick<CSSProperties, 'height'> & {width: number}

/**
 * Returns a mapping of CSS variable names to column widths. Intended for injection
 * into a component's style block to be applied using the getCellStylesFromColumn
 * helper.
 */
export function useColumnWidthVariables<D extends object>(columns: Array<ColumnInstance<D>>): {[key: string]: number} {
  const {columnWidths} = useTableColumnResizing()

  return useMemo(() => {
    const columnWidthVariables: {[key: string]: number} = {}

    for (const column of columns) {
      const resizingWidth: number = columnWidths[column.id] || column.totalWidth

      // When upgrading to React Table v8, revisit this logic. We may be able to do
      // away with the manual checking of minWidth by calling column.getSize().
      columnWidthVariables[columnWidthVariable(column.id)] = Math.max(
        column.minWidth || column.totalWidth,
        resizingWidth,
      )
    }

    return columnWidthVariables
  }, [columns, columnWidths])
}

/**
 * Provides the styles for a block layout cell.
 * Based on TanStack Table v7 API:
 * - docs: https://react-table-v7-docs.netlify.app/docs/api/useblocklayout
 * - source: https://github.com/TanStack/table/blob/v7/src/plugin-hooks/useBlockLayout.js
 */
export const getCellStyles = ({height, width}: CellStyleProps): CSSProperties => ({
  display: 'inline-block',
  boxSizing: 'border-box',
  height,
  width,
})

/**
 * Provides the styles for a block layout cell given a column id
 * Based on TanStack Table v7 API:
 * - docs: https://react-table-v7-docs.netlify.app/docs/api/useblocklayout
 * - source: https://github.com/TanStack/table/blob/v7/src/plugin-hooks/useBlockLayout.js
 */
export const getCellStylesFromColumn = (columnId: string): CSSProperties => ({
  display: 'inline-block',
  boxSizing: 'border-box',
  height: CELL_HEIGHT,
  width: `calc(var(${columnWidthVariable(columnId)}) * 1px)`,
})

type ColSpan = {colSpan: number}
type HeaderCellProps = CellStyleProps & ColSpan
type HeaderCellPropsReturnType = {style: CSSProperties} & ColSpan

/**
 * Provides the styles and properties for a resizable header cell.
 * Based on TanStack Table v7 APIs:
 * - docs: https://react-table-v7-docs.netlify.app/docs/api/useResizeColumns
 * - source: https://github.com/TanStack/table/blob/v7/src/plugin-hooks/useResizeColumns.js
 * - source: https://github.com/TanStack/table/blob/v7/src/makeDefaultPluginHooks.js
 */
export const getHeaderCellProps = (props: HeaderCellProps): HeaderCellPropsReturnType => ({
  style: {
    ...getCellStyles(props),
    position: 'relative',
  },
  colSpan: props.colSpan,
})

type HeaderCellPropsFromColumn = {columnId: string; colSpan: number}
type HeaderCellPropsFromColumnReturnType = {style: CSSProperties; colSpan: number}
/**
 * Provides the styles and properties for a resizable header cell given a column id
 * Based on TanStack Table v7 APIs:
 * - docs: https://react-table-v7-docs.netlify.app/docs/api/useResizeColumns
 * - source: https://github.com/TanStack/table/blob/v7/src/plugin-hooks/useResizeColumns.js
 * - source: https://github.com/TanStack/table/blob/v7/src/makeDefaultPluginHooks.js
 */
export const getHeaderCellPropsFromColumn = ({
  columnId,
  colSpan,
}: HeaderCellPropsFromColumn): HeaderCellPropsFromColumnReturnType => ({
  style: {
    ...getCellStylesFromColumn(columnId),
    height: TABLE_HEADER_HEIGHT,
    position: 'relative',
  },
  colSpan,
})

const columnWidthVariable = (columnId: string): string => `--column-${String(columnId).replace(/\s/g, '-')}-width`
